import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Custom hook for managing persistent filters with URL params and localStorage
 * 
 * @param {Object} defaultFilters - Default filter values
 * @param {string} storageKey - Unique key for localStorage
 * @param {Object} options - Configuration options
 * @param {boolean} options.restoreFromStorage - Whether to restore from localStorage (default: true)
 * @param {number} options.expiryDuration - Duration in milliseconds before filters expire (default: 12 hours)
 * @param {string[]} options.clearOnExpiry - Array of filter keys to clear when expired (default: date/period fields)
 * 
 * @example
 * // Drivers page with 24 hours expiry
 * const { filters, handleFilterChange } = usePersistentFilters(
 *   defaultFilters, 
 *   'drivers-filters',
 *   { expiryDuration: 24 * 60 * 60 * 1000 }
 * );
 * 
 * @example
 * // Trips page with 6 hours expiry and custom fields to clear
 * const { filters, handleFilterChange } = usePersistentFilters(
 *   defaultFilters, 
 *   'trips-filters',
 *   { 
 *     expiryDuration: 6 * 60 * 60 * 1000,
 *     clearOnExpiry: ['startDate', 'endDate', 'period']
 *   }
 * );
 */
const usePersistentFilters = (defaultFilters, storageKey, options = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { 
    restoreFromStorage = true,
    expiryDuration = 12 * 60 * 60 * 1000, // Default: 12 hours in milliseconds
    clearOnExpiry = ['startDate', 'endDate', 'approvedAtDate', 'period'] // Filters to clear on expiry
  } = options;
  
  // Track if filters were expired during initialization using ref
  const wasExpiredRef = useRef(false);
  const expiredFiltersRef = useRef([]);
  
  // Initialize filters from URL params or localStorage
  const getInitialFilters = () => {
    // Check if we've already processed expiry in this session
    const sessionKey = `${storageKey}_expiry_processed`;
    const alreadyProcessed = sessionStorage.getItem(sessionKey);
    
    // First check localStorage expiry before using any cached values
    const stored = localStorage.getItem(storageKey);
    const expiryKey = `${storageKey}_expiry`;
    const storedExpiry = localStorage.getItem(expiryKey);
    let expiredFilters = [];
    let isExpired = false;
    
    // Check expiry FIRST and store the result before any localStorage updates
    if (restoreFromStorage && stored && storedExpiry && !alreadyProcessed) {
      const currentTime = Date.now();
      const expiryTime = parseInt(storedExpiry, 10);
      // const expiryDate = new Date(expiryTime);
      // const currentDate = new Date(currentTime);
      
      // console.log(`[${storageKey}] Current time: ${currentDate.toLocaleString()}`);
      // console.log(`[${storageKey}] Expiry time: ${expiryDate.toLocaleString()}`);
      // console.log(`[${storageKey}] Has expired: ${currentTime > expiryTime}`);
      
      if (currentTime > expiryTime) {
        // console.log(`✓ Filters expired for ${storageKey}. Will clear: ${clearOnExpiry.join(', ')}`);
        isExpired = true;
        expiredFilters = clearOnExpiry;
        // Set ref and sessionStorage flag
        wasExpiredRef.current = true;
        expiredFiltersRef.current = clearOnExpiry;
        sessionStorage.setItem(sessionKey, 'true');
        // console.log(`[${storageKey}] ✓ Marked filters as expired for cleanup`);
      }
    } else if (alreadyProcessed) {
      // We already processed expiry in this session, restore the flag
      wasExpiredRef.current = true;
      expiredFiltersRef.current = clearOnExpiry;
      isExpired = false; // Don't update localStorage again
      // console.log(`[${storageKey}] Already processed expiry in this session`);
    }
    
    // Get filters from URL params
    const urlFilters = {};
    for (const [key, value] of searchParams.entries()) {
      if (defaultFilters.hasOwnProperty(key)) {
        // Skip expired filters from URL
        if (expiredFilters.includes(key)) {
          // console.log(`  - Skipping expired URL param: ${key}`);
          continue;
        }
        
        // Convert boolean strings to actual booleans
        if (typeof defaultFilters[key] === 'boolean') {
          urlFilters[key] = value === 'true';
        } else {
          urlFilters[key] = value;
        }
      }
    }
    
    // Try localStorage
    if (restoreFromStorage && stored) {
      try {
        const parsed = JSON.parse(stored);
        const currentTime = Date.now();
        
        // Apply expiry to localStorage filters (use isExpired flag, not re-checking)
        if (isExpired) {
          expiredFilters.forEach(key => {
            if (parsed.hasOwnProperty(key)) {
              // const oldValue = parsed[key];
              parsed[key] = defaultFilters[key] || '';
              // console.log(`  - Cleared ${key}: "${oldValue}" → "${parsed[key]}"`);
            }
          });
          
          // Save cleaned filters back and update expiry (only on first call)
          if (wasExpiredRef.current && !wasExpiredRef.updated) {
            localStorage.setItem(storageKey, JSON.stringify(parsed));
            const newExpiryTime = currentTime + expiryDuration;
            const expiryKey = `${storageKey}_expiry`;
            localStorage.setItem(expiryKey, newExpiryTime.toString());
            wasExpiredRef.updated = true; // Mark as updated to prevent double-update
            // console.log(`[${storageKey}] New expiry set to: ${new Date(newExpiryTime).toLocaleString()}`);
          }
        } else if (!storedExpiry) {
          // No expiry timestamp found, set one now
          const newExpiryTime = currentTime + expiryDuration;
          const expiryKey = `${storageKey}_expiry`;
          localStorage.setItem(expiryKey, newExpiryTime.toString());
          // console.log(`[${storageKey}] No expiry found. Setting to: ${new Date(newExpiryTime).toLocaleString()}`);
        }
        
        // Migrate known misspelled keys from older versions
        if (parsed.verficationNeeded !== undefined && parsed.verificationNeeded === undefined) {
          parsed.verificationNeeded = parsed.verficationNeeded;
        }
        
        // Merge: defaultFilters < localStorage < URL params (URL has highest priority for non-expired fields)
        const processedFilters = { ...defaultFilters, ...parsed, ...urlFilters };
        
        // Convert boolean fields to actual booleans
        Object.keys(defaultFilters).forEach(key => {
          if (typeof defaultFilters[key] === 'boolean' && processedFilters[key] !== undefined) {
            processedFilters[key] = Boolean(processedFilters[key]);
          }
        });
        
        return processedFilters;
      } catch (error) {
        console.error('Error reading from localStorage:', error);
      }
    }
    
    // Fallback: merge defaults with URL filters
    return { ...defaultFilters, ...urlFilters };
  };

  const [filters, setFilters] = useState(getInitialFilters);

  // Clean up expired URL params on mount and update filters state
  useEffect(() => {
    // Only clean if filters were expired during initialization
    if (!wasExpiredRef.current) {
      // console.log(`[${storageKey}] Filters not expired, skipping cleanup`);
      return;
    }
    
    // console.log(`[${storageKey}] Running post-initialization cleanup for expired filters`);
    
    // Remove expired filter params from URL
    const currentParams = new URLSearchParams(window.location.search);
    let hasExpiredParams = false;
    let needsStateUpdate = false;
    const clearedFilters = { ...filters };
    
    expiredFiltersRef.current.forEach(key => {
      // Check and remove from URL
      if (currentParams.has(key)) {
        // const oldValue = currentParams.get(key);
        currentParams.delete(key);
        hasExpiredParams = true;
        // console.log(`  - Removing expired URL param: ${key} = ${oldValue}`);
      }
      
      // Check and clear from state
      if (filters[key] && filters[key] !== defaultFilters[key]) {
        // console.log(`  - Clearing filter state: ${key} = ${filters[key]} → ${defaultFilters[key] || ''}`);
        clearedFilters[key] = defaultFilters[key] || '';
        needsStateUpdate = true;
      }
    });
    
    // Update state if needed
    if (needsStateUpdate) {
      setFilters(clearedFilters);
      // console.log(`[${storageKey}] ✓ Updated filter state`);
    }
    
    // Update URL if needed
    if (hasExpiredParams) {
      setSearchParams(currentParams, { replace: true });
      // console.log(`[${storageKey}] ✓ Cleaned expired URL parameters from browser`);
    }
    
    // if (!hasExpiredParams && !needsStateUpdate) {
    //   // console.log(`[${storageKey}] No additional cleanup needed`);
    // }
    
    // Clear the session flag after cleanup is complete
    const sessionKey = `${storageKey}_expiry_processed`;
    sessionStorage.removeItem(sessionKey);
    // console.log(`[${storageKey}] ✓ Cleanup complete, ready for next expiry check`);
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Update URL params and localStorage when filters change
  const updateFilters = (newFilters) => {
    setFilters(newFilters);
    
    // Update URL params without navigation, preserving other unrelated parameters (like tab)
    const newSearchParams = new URLSearchParams(window.location.search);
    
    // First, clear existing filter keys from search parameters to avoid duplication/stale data
    Object.keys(defaultFilters).forEach(key => {
      newSearchParams.delete(key);
    });

    // Then, set new filter values
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        newSearchParams.set(key, value.toString());
      }
    });
    setSearchParams(newSearchParams, { replace: true });
    
    // Update localStorage with new expiry timestamp
    try {
      const currentTime = Date.now();
      const expiryTime = currentTime + expiryDuration;
      const expiryKey = `${storageKey}_expiry`;
      
      localStorage.setItem(storageKey, JSON.stringify(newFilters));
      localStorage.setItem(expiryKey, expiryTime.toString());
    } catch (error) {
      console.error('Error writing to localStorage:', error);
    }
  };

  // Handler for individual filter changes
  const handleFilterChange = (key, value) => {
    const newFilters = {
      ...filters,
      [key]: value,
      page: 1 // Reset to first page when filters change
    };
    updateFilters(newFilters);
  };

  // Handler for page changes
  const handlePageChange = (page) => {
    const newFilters = { ...filters, page };
    updateFilters(newFilters);
  };

  // Clear all filters
  const clearFilters = () => {
    updateFilters(defaultFilters);
  };

  // Reset to default filters
  const resetFilters = () => {
    updateFilters(defaultFilters);
  };

  return {
    filters,
    handleFilterChange,
    handlePageChange,
    clearFilters,
    resetFilters,
    updateFilters
  };
};

export default usePersistentFilters; 